import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiPlus } from "react-icons/fi";
import SortableTable from "../../Componentes/Utils/SortableTable";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";
import ModalAdicionarUsuario, { MODAL_ID as MODAL_ADICIONAR_ID } from "./ModalAdicionarUsuario";
import ModalResetarSenha, { MODAL_ID as MODAL_RESETAR_SENHA_ID } from "./ModalResetarSenha";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;
const SET_ATIVO_URL = "/Painel/API/AccessManagement/GestaoUsuarios/SetAtivo/";
const SET_BLOQUEADO_URL = "/Painel/API/AccessManagement/GestaoUsuarios/SetBloqueado/";

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const HeaderBar = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: nowrap;
`;

const FilterInput = styled.input`
    height: 40px;
    flex: 1;
    min-width: 240px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
`;

const AddButton = styled.button`
    height: 40px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(0, 123, 255, 0.85);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover {
        opacity: 0.9;
    }
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const STATUS_COLORS = {
    ativo: { color: "#16a34a", background: "#dcfce7" },
    bloqueado: { color: "#ca8a04", background: "#fef9c3" },
    inativo: { color: "#dc2626", background: "#fee2e2" },
};

const StatusCell = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: 0;
    background-color: ${(p) => STATUS_COLORS[p.$status]?.background || STATUS_COLORS.inativo.background};
    color: ${(p) => STATUS_COLORS[p.$status]?.color || STATUS_COLORS.inativo.color};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
`;

const StatusDot = styled.span`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${(p) => STATUS_COLORS[p.$status]?.color || STATUS_COLORS.inativo.color};
    flex-shrink: 0;
`;

const ActionsCell = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
`;

const KebabButton = styled.button`
    all: unset;
    cursor: pointer;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #242d34;
    font-size: 18px;
    line-height: 1;

    &:hover {
        background: rgba(36, 45, 52, 0.08);
    }

    &:focus-visible {
        outline: 2px solid #007bff;
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Menu = styled.div`
    position: absolute;
    top: 38px;
    right: 0;
    width: 200px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    box-shadow: 0 12px 26px rgba(0, 0, 0, 0.18);
    border-radius: 12px;
    overflow: hidden;
    z-index: 10;
`;

const MenuItem = styled.button`
    all: unset;
    display: flex;
    width: 100%;
    padding: 12px;
    box-sizing: border-box;
    cursor: pointer;
    color: #242d34;
    font-size: 13px;

    &:hover {
        background: #e8f0fe;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

function getUserStatus(row) {
    if (Number(row?.bloqueado) === 1) return { key: "bloqueado", label: "Bloqueado" };
    if (Number(row?.ativo) === 1) return { key: "ativo", label: "Ativo" };
    return { key: "inativo", label: "Inativo" };
}

function formatUltimoLogin(value) {
    if (!value) return "—";
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function buildSearchHaystack(row) {
    if (!row) return "";

    const status = getUserStatus(row);

    const deveTrocarSenha = Number(row?.deve_trocar_senha) === 1 ? "Sim" : "Não";

    return [
        row.usuario,
        row.user_id,
        row.email,
        row.grupo_nome,
        status.label,
        deveTrocarSenha,
        formatUltimoLogin(row.ultimo_login),
    ]
        .filter((v) => v != null && String(v).trim() !== "" && String(v).trim() !== "—")
        .join(" ")
        .toUpperCase();
}

const GestaoUsuarios = () => {
    const api = useApi();
    const { openModal, isModalOpen } = useModal();
    const { notifySuccess, notifyError } = useToast();
    const tableRef = useRef(null);
    const menuRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [q, setQ] = useState("");
    const [openMenuId, setOpenMenuId] = useState(null);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [resetUser, setResetUser] = useState(null);

    const fetchUsuarios = useCallback(async () => {
        setLoading(true);

        try {
            const { data } = await api.get("/Painel/API/AccessManagement/GestaoUsuarios/List/index.php");
            setUsuarios(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setUsuarios([]);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchUsuarios();
    }, [fetchUsuarios]);

    useEffect(() => {
        function onDocClick(e) {
            if (!openMenuId) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setOpenMenuId(null);
        }

        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [openMenuId]);

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();
        if (!query) return usuarios;

        return usuarios.filter((row) => buildSearchHaystack(row).includes(query));
    }, [usuarios, q]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filtered]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = filtered.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const handleSetAtivo = useCallback(
        async (row, ativo) => {
            if (!row?.id || actionLoadingId != null) return;

            setActionLoadingId(row.id);
            setOpenMenuId(null);

            try {
                const { data } = await api.post(SET_ATIVO_URL, { id: row.id, ativo });
                setUsuarios((prev) =>
                    prev.map((u) => (Number(u.id) === Number(row.id) ? { ...u, ativo } : u))
                );
                notifySuccess(data?.message || (ativo === 1 ? "Usuário reativado." : "Usuário inativado."));
            } catch (error) {
                console.error(error);
                notifyError(error?.response?.data?.message || "Erro ao atualizar status do usuário.");
            } finally {
                setActionLoadingId(null);
            }
        },
        [actionLoadingId, api, notifyError, notifySuccess]
    );

    const handleSetBloqueado = useCallback(
        async (row, bloqueado) => {
            if (!row?.id || actionLoadingId != null) return;

            setActionLoadingId(row.id);
            setOpenMenuId(null);

            try {
                const { data } = await api.post(SET_BLOQUEADO_URL, { id: row.id, bloqueado });
                setUsuarios((prev) =>
                    prev.map((u) => (Number(u.id) === Number(row.id) ? { ...u, bloqueado } : u))
                );
                notifySuccess(
                    data?.message || (bloqueado === 1 ? "Usuário bloqueado." : "Usuário desbloqueado.")
                );
            } catch (error) {
                console.error(error);
                notifyError(error?.response?.data?.message || "Erro ao atualizar bloqueio do usuário.");
            } finally {
                setActionLoadingId(null);
            }
        },
        [actionLoadingId, api, notifyError, notifySuccess]
    );

    const handleOpenResetSenha = useCallback(
        (row) => {
            setOpenMenuId(null);
            setResetUser(row);
            openModal(MODAL_RESETAR_SENHA_ID);
        },
        [openModal]
    );

    const columns = useMemo(
        () => [
            {
                id: "usuario",
                header: "NOME",
                accessor: "usuario",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "user_id",
                header: "USUÁRIO",
                accessor: "user_id",
                sortable: true,
                width: "180px",
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "email",
                header: "E-MAIL",
                accessor: "email",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "grupo_nome",
                header: "GRUPO",
                accessor: "grupo_nome",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
            {
                id: "status",
                header: "STATUS",
                accessor: (row) => getUserStatus(row).label,
                sortable: true,
                width: "140px",
                render: (_value, row) => {
                    const status = getUserStatus(row);
                    return (
                        <StatusCell $status={status.key}>
                            <StatusDot $status={status.key} />
                            {status.label}
                        </StatusCell>
                    );
                },
            },
            {
                id: "deve_trocar_senha",
                header: "TROCAR SENHA",
                accessor: (row) => (Number(row?.deve_trocar_senha) === 1 ? "Sim" : "Não"),
                sortable: true,
                width: "140px",
                render: (_value, row) => (Number(row?.deve_trocar_senha) === 1 ? "Sim" : "Não"),
            },
            {
                id: "ultimo_login",
                header: "ÚLTIMO LOGIN",
                accessor: "ultimo_login",
                sortable: true,
                width: "180px",
                render: (value) => formatUltimoLogin(value),
            },
            {
                id: "acoes",
                header: "",
                accessor: () => null,
                sortable: false,
                width: "60px",
                align: "center",
                render: (_value, row) => {
                    const isActive = Number(row.ativo) === 1;
                    const isBlocked = Number(row.bloqueado) === 1;
                    const isBusy = actionLoadingId === row.id;

                    return (
                        <ActionsCell
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <KebabButton
                                type="button"
                                title="Ações"
                                aria-label={`Ações do usuário ${row.usuario || row.id}`}
                                disabled={isBusy}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId((prev) => (prev === row.id ? null : row.id));
                                }}
                            >
                                ⋯
                            </KebabButton>

                            {openMenuId === row.id && (
                                <Menu
                                    ref={menuRef}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <MenuItem
                                        type="button"
                                        disabled={isBusy}
                                        onClick={() => handleSetAtivo(row, isActive ? 0 : 1)}
                                    >
                                        {isActive ? "Inativar usuário" : "Reativar usuário"}
                                    </MenuItem>
                                    <MenuItem
                                        type="button"
                                        disabled={isBusy}
                                        onClick={() => handleSetBloqueado(row, isBlocked ? 0 : 1)}
                                    >
                                        {isBlocked ? "Desbloquear usuário" : "Bloquear usuário"}
                                    </MenuItem>
                                    <MenuItem
                                        type="button"
                                        disabled={isBusy}
                                        onClick={() => handleOpenResetSenha(row)}
                                    >
                                        Resetar senha
                                    </MenuItem>
                                </Menu>
                            )}
                        </ActionsCell>
                    );
                },
            },
        ],
        [actionLoadingId, handleSetAtivo, handleSetBloqueado, handleOpenResetSenha, openMenuId]
    );

    return (
        <PageContent>
            <HeaderBar>
                <Badge>Total: {loading ? "..." : filtered.length}</Badge>
                <FilterInput
                    placeholder="Pesquisar por nome, usuário, e-mail, grupo ou status..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <AddButton type="button" onClick={() => openModal(MODAL_ADICIONAR_ID)}>
                    <FiPlus size={16} />
                    Adicionar usuário
                </AddButton>
            </HeaderBar>

            <TableShell>
                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        getRowId={(row) => row.id}
                        isLoading={loading}
                        loadingText="Carregando usuários..."
                        emptyText="NENHUM USUÁRIO ENCONTRADO"
                        initialSort={{ key: "usuario", direction: "asc" }}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                    />
                </TableFlexFill>
            </TableShell>

            {isModalOpen(MODAL_ADICIONAR_ID) && <ModalAdicionarUsuario onSaved={fetchUsuarios} />}

            {isModalOpen(MODAL_RESETAR_SENHA_ID) && resetUser && (
                <ModalResetarSenha usuario={resetUser} onSaved={fetchUsuarios} />
            )}
        </PageContent>
    );
};

export default GestaoUsuarios;
